import {useMemo} from 'react';

import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import TableUtil from 'notebook/tables/TableUtil';

import {StructuredTable, StructuredTableAdapter} from '../StructuredTableConstants';

/**
 * Provides a callback to determine whether a selected range of a structured table's cells can be merged. The
 * returned function applies common logic for all structured tables:
 * - No merging across columns
 * - No merging in columns with custom eval
 * - No merging of computed cells
 * - No merging across archived rows
 * - No merging if table/column/any cells are locked, read only, or archived
 *
 * The returned function performs basic sanity checking on the input boundaries to handle scenarios where
 * data is being pasted in from another table. To this end, it is tolerant of rows greater than the current
 * table height but intolerant of column dimensions greater than the table's width.
 */
export default function useAllowMergeSelectionCallback<TTableMetadata, TRowMetadata>(
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
  structuredTableAdapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>
): (multiStartIndex: TableIndex, multiEndIndex: TableIndex) => boolean {
  return useMemo(
    () =>
      (multiStartIndex: TableIndex, multiEndIndex: TableIndex): boolean => {
        const {minI, minJ, maxI, maxJ} = TableUtil.indicesToCorners(multiStartIndex, multiEndIndex);
        if (minJ !== maxJ) {
          return false;
        }

        if (
          minI < 0 ||
          minJ < 0 ||
          minJ >= structuredTable.columns.length ||
          maxJ >= structuredTable.columns.length
        ) {
          return false;
        }

        const col = structuredTable.columns[minJ];
        if (!structuredTableAdapter.allowMergedCellRange(structuredTable, col)) {
          return false;
        }

        for (let i = minI; i <= Math.min(maxI, structuredTable.rows.length - 1); i++) {
          if (
            structuredTable.rows[i].lastSyncedArchivePurpose != null ||
            structuredTable.rows[i].cells[minJ].locked
          ) {
            return false;
          }
        }

        return true;
      },
    [structuredTable, structuredTableAdapter]
  );
}
